import React, {useReducer, useState} from 'react';
import './App.css';
import {
    Container,
    LeftWrapper,
    ImgWrapper,
    LogoImg,
    OnboardingImg,
    Content,
    Main, Title, Subtitle, Form, Hint
} from "./App.styled";
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {Button, CssBaseline, Link, TextField, ThemeProvider} from "@mui/material";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {gen2Theme} from "./theme";
import axios, {AxiosError, AxiosResponse} from "axios";
import G2Link from "./components/G2Link";

export interface IGlobalLoginEmail {
    email: string;
}

export interface IAccounts {
    g1: string[];
    g2: string[];
}

interface IRegions {
    regionName: string;
    link: string;
}

function App() {

    const schema = Yup.object().shape({
        email: Yup.string().email('email.invalid').required('email.required'),
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [messages, setMessages] = useState<string[]>([]);
    const [g1Regions, setG1Regions] = useState<IRegions[]>([]);
    const [g2Link, setG2Link] = useState<string|null>(null);

    const [ignored, forceUpdate] = useReducer(x => x + 1, 0);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IGlobalLoginEmail>({
        defaultValues: {
            email: '',
        },
        resolver: yupResolver(schema),
    });

    const onSubmit: SubmitHandler<IGlobalLoginEmail> = (form) => {
        setIsLoading(true);

        axios.post('/login', {
            email: form.email,
        })
            .then((response: AxiosResponse) => {

                const accounts = response.data as IAccounts;

                if (accounts.g1.length == 1 && accounts.g2.length == 0) {
                    window.location.href = 'https://' + accounts.g1[0] + '/login?email=' + encodeURIComponent(form.email);
                    return
                }
                if (accounts.g2.length >= 1) {

                    const g2Link = 'https://fileinvite.app/login?email=' + encodeURIComponent(form.email) + '&region=' + encodeURIComponent(accounts.g2[0]);

                    if (accounts.g2.length == 1 && accounts.g1.length == 0) {
                        window.location.href = g2Link;
                        return
                    }

                    setG2Link(g2Link)
                }

                accounts.g1.forEach((account) => {

                    var region = ''
                    switch (account) {
                        case 'app.fileinvite.com':
                            region = 'Global'
                            break;
                        case 'au.fileinvite.com':
                            region = 'Australia'
                            break;
                        case 'us.fileinvite.com':
                            region = 'United States'
                            break;
                        case 'uk.fileinvite.com':
                            region = 'United Kingdom'
                            break;
                    }

                    g1Regions.push({
                        regionName: region,
                        link: 'https://' + account + '/login?email=' + encodeURIComponent(form.email)
                    })
                })

                setIsLoaded(true);
                forceUpdate()
            })
            .catch((error: AxiosError) => {
                console.log(error);
            })
            .finally(() => {
                setIsLoading(false);
            })

    };

    if (isLoaded) {
        return (
            <ThemeProvider theme={gen2Theme}>
                <CssBaseline>
                    <Container>
                        <LeftWrapper>
                            <ImgWrapper>
                                <LogoImg src="/assets/logo-lg.svg" alt="logo"/>
                                <OnboardingImg
                                    src='/assets/onboarding-image.svg'
                                    alt="onboarding-image"
                                />
                            </ImgWrapper>
                        </LeftWrapper>
                        <Content>
                            <Main>
                                <header>
                                    <Subtitle data-cy="subtitle">
                                        You have multiple accounts in different regions
                                    </Subtitle>
                                </header>

                                <G2Link g2link={g2Link}></G2Link>

                                {g1Regions.map((region) => {
                                    return (
                                        <a href={region.link}>Sign in to FileInvite Classic ({region.regionName})</a>
                                    )
                                })}

                            </Main>
                        </Content>
                    </Container>
                </CssBaseline>
            </ThemeProvider>
        );
    } else {
        return (
            <ThemeProvider theme={gen2Theme}>
                <CssBaseline>
                    <Container>
                        <LeftWrapper>
                            <ImgWrapper>
                                <LogoImg src="/assets/logo-lg.svg" alt="logo"/>
                                <OnboardingImg
                                    src='/assets/onboarding-image.svg'
                                    alt="onboarding-image"
                                />
                            </ImgWrapper>
                        </LeftWrapper>
                        <Content>
                            <Main>
                                <header>
                                    <Title data-cy="title">
                                        Sign in
                                    </Title>
                                    <Subtitle data-cy="subtitle">
                                        to continue to Fileinvite
                                    </Subtitle>
                                </header>
                                <Form noValidate>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                id="email"
                                                fullWidth
                                                required
                                                label='Email Address'
                                                error={Boolean(errors.email)}
                                                helperText={errors.email?.message && 'Please provide a valid email address'}
                                            />
                                        )}
                                    />
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        loading={isLoading}
                                        type="submit"
                                        onClick={handleSubmit(onSubmit)}
                                        data-cy="next"
                                    >
                                        Next
                                    </LoadingButton>
                                </Form>
                            </Main>
                        </Content>
                    </Container>
                </CssBaseline>
            </ThemeProvider>
        );
    }
}

export default App;
